/**
 * AiAdvisoryChip  —  AI-000-E
 *
 * Surface-agnostic advisory chip. Every AI surface (CAPA cluster, BMR review,
 * OOS guide, dashboard advisory, etc.) renders one of these and gets:
 *
 *   - Annex 22 disclaimer (always visible).
 *   - Provider badge (gemini / anthropic / rule-based).
 *   - Summary + concerns block.
 *   - Citations as clickable badges (open the source record).
 *   - Feedback buttons: 👍 useful, 👎 noise, ⚠ wrong, ✎ note.
 *   - Hidden invocation_id so Inspector Mode can replay the AI chain.
 *
 * API contract (mock-api `/api/v1/ai/advisory`):
 *   POST { surface, prompt, citations?, jsonSchema?, cacheKey? }
 *     → { response: { advisory_only, surface, payload, citations,
 *                     annex22_disclaimer }, invocationId, provider }
 *
 * POST `/api/v1/ai/feedback`:
 *   { invocationId, verdict: 'useful'|'noise'|'wrong'|'ignored', freeText? }
 */

import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import styles from './AiAdvisoryChip.module.css';

export type AdvisoryVerdict = 'useful' | 'noise' | 'wrong' | 'ignored';

export interface AdvisoryCitation {
  kind: string;
  id: string;
  label?: string;
  href?: string;
}

export interface AdvisoryPayload {
  summary?: string;
  concerns?: string[];
  text?: string;
  // Surface-specific extras pass through untouched so the chip stays
  // surface-agnostic; the caller can render `extras` how they like.
  [key: string]: unknown;
}

export interface AdvisoryResponse {
  advisory_only: true;
  surface: string;
  payload: AdvisoryPayload;
  citations: AdvisoryCitation[];
  annex22_disclaimer: string;
}

export interface AiAdvisoryChipProps {
  surface: string;
  prompt: string;
  citations?: AdvisoryCitation[];
  cacheKey?: string;
  jsonSchema?: Record<string, unknown>;
  /** Override the API base if the chip lives outside the SPA's normal config. */
  apiBase?: string;
  /**
   * Manual rendering override for the payload area — useful for surfaces that
   * want richer than {summary, concerns}. Falls back to default rendering.
   */
  renderPayload?: (payload: AdvisoryPayload, citations: AdvisoryCitation[]) => React.ReactNode;
  /** Trigger refetch when this changes (e.g. workflow_id changes). */
  refreshKey?: string | number;
  /** Compact mode — no card frame, useful inline. */
  compact?: boolean;
  className?: string;
}

interface AdvisoryEnvelope {
  response: AdvisoryResponse;
  invocationId: string;
  provider: string;
}

const DEFAULT_DISCLAIMER =
  'AI advisory only — verify against source records before acting. AI never signs or approves.';

export function AiAdvisoryChip(props: AiAdvisoryChipProps) {
  const {
    surface, prompt, citations: requestCitations, cacheKey, jsonSchema,
    apiBase, renderPayload, refreshKey, compact, className,
  } = props;

  const [state, setState] = useState<{
    status: 'idle' | 'loading' | 'ready' | 'error';
    envelope?: AdvisoryEnvelope;
    error?: string;
  }>({ status: 'idle' });

  const [verdict, setVerdict] = useState<AdvisoryVerdict | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');

  const apiPrefix = apiBase ?? '';

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    fetchAdvisory(apiPrefix, { surface, prompt, citations: requestCitations, cacheKey, jsonSchema })
      .then((env) => { if (!cancelled) setState({ status: 'ready', envelope: env }); })
      .catch((err) => { if (!cancelled) setState({ status: 'error', error: humanError(err) }); });
    return () => { cancelled = true; };
  // refreshKey deliberately scopes the cache miss; surface/prompt are stable per mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surface, refreshKey]);

  const env = state.envelope;
  const payload = env?.response.payload;
  const citationsFinal = env?.response.citations || requestCitations || [];
  const disclaimer = env?.response.annex22_disclaimer || DEFAULT_DISCLAIMER;
  const provider = env?.provider || 'pending';

  const submitFeedback = (v: AdvisoryVerdict) => {
    if (!env) return;
    setVerdict(v);
    void postFeedback(apiPrefix, { invocationId: env.invocationId, verdict: v, freeText: note || undefined });
  };

  const submitNote = () => {
    if (!env || !note.trim()) return;
    setVerdict('useful');
    void postFeedback(apiPrefix, { invocationId: env.invocationId, verdict: 'useful', freeText: note });
    setNoteOpen(false);
  };

  return (
    <aside
      role="complementary"
      aria-label={`AI advisory — ${surface}`}
      data-surface={surface}
      data-invocation-id={env?.invocationId}
      className={clsx(styles.chip, compact && styles.compact, className)}
    >
      <header className={styles.header}>
        <span className={styles.icon} aria-hidden>✦</span>
        <span className={styles.title}>AI advisory</span>
        <span className={clsx(styles.providerBadge, styles[`provider_${provider}`])}>
          {provider === 'mock' || provider === 'rule-based' || provider === 'pending' ? provider : 'Cloud AI'}
        </span>
      </header>

      {state.status === 'loading' && (
        <div className={styles.body}>
          <Skeleton lines={3} />
        </div>
      )}

      {state.status === 'error' && (
        <div className={styles.body}>
          <p className={styles.errorLine} role="alert">
            Advisory unavailable: {state.error}. Rule-based fallback would normally appear here.
          </p>
        </div>
      )}

      {state.status === 'ready' && payload && (
        <div className={styles.body}>
          {renderPayload ? renderPayload(payload, citationsFinal) : <DefaultPayloadView payload={payload} />}
          {citationsFinal.length > 0 && (
            <ul className={styles.citations} aria-label="cited records">
              {citationsFinal.map((c) => (
                <li key={`${c.kind}:${c.id}`}>
                  {c.href
                    ? <a href={c.href} className={styles.citationLink}>{c.label || `${c.kind}:${c.id}`}</a>
                    : <span className={styles.citationLink}>{c.label || `${c.kind}:${c.id}`}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <footer className={styles.footer}>
        <p className={styles.disclaimer}>{disclaimer}</p>
        {env && (
          <div className={styles.feedbackRow} role="group" aria-label="advisory feedback">
            <button
              type="button"
              aria-pressed={verdict === 'useful'}
              className={clsx(styles.fbButton, verdict === 'useful' && styles.fbActive)}
              onClick={() => submitFeedback('useful')}
              title="Useful"
            >👍</button>
            <button
              type="button"
              aria-pressed={verdict === 'noise'}
              className={clsx(styles.fbButton, verdict === 'noise' && styles.fbActive)}
              onClick={() => submitFeedback('noise')}
              title="Noise"
            >👎</button>
            <button
              type="button"
              aria-pressed={verdict === 'wrong'}
              className={clsx(styles.fbButton, verdict === 'wrong' && styles.fbActive)}
              onClick={() => submitFeedback('wrong')}
              title="Wrong"
            >⚠</button>
            <button
              type="button"
              aria-pressed={noteOpen}
              className={clsx(styles.fbButton, noteOpen && styles.fbActive)}
              onClick={() => setNoteOpen((o) => !o)}
              title="Add a note"
            >✎</button>
          </div>
        )}
        {noteOpen && (
          <div className={styles.noteRow}>
            <textarea
              className={styles.noteInput}
              placeholder="Optional note about this advisory…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
            />
            <button type="button" className={styles.noteSubmit} onClick={submitNote}>Send</button>
          </div>
        )}
      </footer>
    </aside>
  );
}

function DefaultPayloadView({ payload }: { payload: AdvisoryPayload }) {
  const summary = (payload.summary as string | undefined) || (payload.text as string | undefined);
  const concerns = Array.isArray(payload.concerns) ? (payload.concerns as string[]) : [];
  return (
    <>
      {summary && <p className={styles.summary}>{summary}</p>}
      {concerns.length > 0 && (
        <ul className={styles.concerns}>
          {concerns.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      )}
    </>
  );
}

function Skeleton({ lines }: { lines: number }) {
  return (
    <div className={styles.skeleton} aria-hidden>
      {Array.from({ length: lines }).map((_, i) => (
        <span key={i} className={styles.skeletonLine} style={{ width: `${70 - i * 10}%` }} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  API helpers — small fetch wrappers, no react-query dep
// ---------------------------------------------------------------------------
async function fetchAdvisory(
  apiBase: string,
  body: Pick<AiAdvisoryChipProps, 'surface' | 'prompt' | 'citations' | 'cacheKey' | 'jsonSchema'>,
): Promise<AdvisoryEnvelope> {
  const { buildHeaders, apiUrl } = await import('../../api/client');
  const res = await fetch(apiBase ? `${apiBase}/api/v1/ai/advisory` : apiUrl('/api/v1/ai/advisory'), {
    method: 'POST',
    headers: buildHeaders({ json: true }),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`advisory ${res.status}`);
  const data = (await res.json()) as AdvisoryEnvelope;
  if (!data?.response?.advisory_only) throw new Error('response missing advisory_only flag');
  return data;
}

async function postFeedback(
  apiBase: string,
  body: { invocationId: string; verdict: AdvisoryVerdict; freeText?: string },
): Promise<void> {
  const { buildHeaders, apiUrl } = await import('../../api/client');
  await fetch(apiBase ? `${apiBase}/api/v1/ai/feedback` : apiUrl('/api/v1/ai/feedback'), {
    method: 'POST',
    headers: buildHeaders({ json: true }),
    body: JSON.stringify(body),
  });
}

function humanError(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}
